import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Mail, Send, RefreshCw, Eye, FileClock, AlertCircle, CheckCircle2,
} from 'lucide-react';

const GREEN = '#0F6E56';

function StatPill({ label, value, accent = 'emerald' }) {
  const color = {
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
    gray: 'bg-gray-100 text-gray-600',
  }[accent];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${color}`}>
      {label}: {value ?? '—'}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function Notifications({ user }) {
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingMe, setSendingMe] = useState(false);
  const [runningAll, setRunningAll] = useState(false);
  const [lastRunSummary, setLastRunSummary] = useState(null);

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data } = await api.get('/notifications/logs?limit=50');
      setLogs(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/notifications/logs?limit=50');
        if (!cancelled) setLogs(data.items || []);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Erro ao carregar logs');
      } finally {
        if (!cancelled) setLoadingLogs(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handlePreview = () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = sessionStorage.getItem('access_token');
    // Open the HTML preview in a new tab. We pass the token via a fetch-then-blob
    // dance because we can't set Authorization headers on a plain window.open.
    fetch(`${BACKEND_URL}/api/notifications/weekly-digest/preview`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('Falha ao carregar pré-visualização');
        return r.text();
      })
      .then((html) => {
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Revoke after a short while so the new tab has time to read it
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch((err) => toast.error(err.message || 'Erro na pré-visualização'));
  };

  const handleSendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Informe um e-mail válido');
      return;
    }
    setSendingTest(true);
    try {
      const { data } = await api.post('/notifications/weekly-digest/send-test', {
        to: testEmail,
      });
      toast.success(`E-mail enviado para ${data.to}`, {
        description: `ID Resend: ${data.resend_id}`,
      });
      await loadLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao enviar e-mail de teste');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendMe = async () => {
    setSendingMe(true);
    try {
      const { data } = await api.post('/notifications/weekly-digest/send-me');
      toast.success(`Digest enviado para ${data.to}`, {
        description: `ID Resend: ${data.resend_id}`,
      });
      await loadLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao enviar');
    } finally {
      setSendingMe(false);
    }
  };

  const handleRunAll = async () => {
    if (!window.confirm(
      'Confirmar disparo do e-mail semanal AGORA para TODOS os consultores ativos?',
    )) return;
    setRunningAll(true);
    try {
      const { data } = await api.post('/notifications/weekly-digest/run-now');
      setLastRunSummary(data);
      toast.success(
        `Job concluído — ${data.sent}/${data.total_consultants} envios bem-sucedidos`,
        { description: data.failed > 0 ? `${data.failed} falharam` : 'Todos enviados' },
      );
      await loadLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao executar o job');
    } finally {
      setRunningAll(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="notifications-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ backgroundColor: '#E1F5EE', color: GREEN }}>
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Notificações por E-mail</h1>
            <p className="text-sm text-gray-500">
              Gerenciar o envio do digest semanal &ldquo;Sua Semana em Números&rdquo;
            </p>
          </div>
        </div>

        {/* Schedule info */}
        <Card className="p-4 border border-emerald-200 bg-emerald-50/40">
          <div className="flex items-start gap-3">
            <FileClock className="w-5 h-5 mt-0.5" style={{ color: GREEN }} />
            <div className="text-sm text-gray-700">
              <div className="font-medium text-gray-900 mb-1">Agendamento automático ativo</div>
              <div>
                Toda <strong>segunda-feira às 07:00</strong> (horário de Brasília), o sistema envia
                automaticamente o digest para todos os consultores ativos.
                Remetente: <code className="px-1 py-0.5 bg-white border border-gray-200 rounded text-xs">sistema@quatrofolhas.app</code>
              </div>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <Card className="p-6">
          <h2 className="text-base font-medium text-gray-900 mb-4">Ações</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Preview */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-4 h-4 text-gray-600" />
                <div className="text-sm font-medium text-gray-900">Pré-visualizar HTML</div>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Abre o e-mail renderizado em uma nova aba (sem enviar).
              </p>
              <Button
                onClick={handlePreview}
                variant="outline"
                size="sm"
                data-testid="notifications-preview-button"
              >
                <Eye className="w-4 h-4 mr-1.5" /> Visualizar
              </Button>
            </div>

            {/* Send me */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Send className="w-4 h-4 text-gray-600" />
                <div className="text-sm font-medium text-gray-900">Enviar para mim</div>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Envia um digest com <strong>suas próprias</strong> estatísticas para {user?.email || 'seu e-mail'}.
              </p>
              <Button
                onClick={handleSendMe}
                disabled={sendingMe}
                size="sm"
                style={{ backgroundColor: GREEN }}
                className="text-white hover:opacity-90"
                data-testid="notifications-send-me-button"
              >
                {sendingMe ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Enviar para mim
              </Button>
            </div>

            {/* Send test to any email (admin only) */}
            {user?.role === 'ADMIN' && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Send className="w-4 h-4 text-gray-600" />
                  <div className="text-sm font-medium text-gray-900">Enviar e-mail de teste</div>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Envia para qualquer endereço usando os dados do primeiro consultor ativo.
                </p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="destinatario@exemplo.com"
                    className="text-sm"
                    data-testid="notifications-test-email-input"
                  />
                  <Button
                    onClick={handleSendTest}
                    disabled={sendingTest}
                    size="sm"
                    style={{ backgroundColor: GREEN }}
                    className="text-white hover:opacity-90 shrink-0"
                    data-testid="notifications-send-test-button"
                  >
                    {sendingTest ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}

            {/* Run-now (admin only) */}
            {user?.role === 'ADMIN' && (
              <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4 text-amber-700" />
                  <div className="text-sm font-medium text-gray-900">Disparar para todos agora</div>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  Executa o job de segunda-feira <strong>imediatamente</strong> para todos os consultores ativos.
                </p>
                <Button
                  onClick={handleRunAll}
                  disabled={runningAll}
                  size="sm"
                  variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-100"
                  data-testid="notifications-run-now-button"
                >
                  {runningAll ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                  Disparar agora
                </Button>
              </div>
            )}
          </div>

          {lastRunSummary && (
            <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700">
              <div className="font-medium mb-1">Último disparo manual:</div>
              <div className="flex flex-wrap gap-2">
                <StatPill label="Consultores" value={lastRunSummary.total_consultants} accent="gray" />
                <StatPill label="Enviados" value={lastRunSummary.sent} accent="emerald" />
                <StatPill label="Falharam" value={lastRunSummary.failed} accent={lastRunSummary.failed ? 'red' : 'gray'} />
              </div>
            </div>
          )}
        </Card>

        {/* Logs */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-medium text-gray-900">Histórico de envios</h2>
              <p className="text-xs text-gray-500">Últimos 50 disparos do digest semanal</p>
            </div>
            <Button
              onClick={loadLogs}
              variant="outline"
              size="sm"
              data-testid="notifications-refresh-logs"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loadingLogs ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>

          {loadingLogs ? (
            <div className="py-8 text-center text-sm text-gray-500">Carregando histórico...</div>
          ) : logs.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              <FileClock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              Nenhum envio registrado ainda.
              <div className="text-xs mt-1">Use os botões acima para disparar um envio.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                    <th className="py-2 pr-3">Data/Hora</th>
                    <th className="py-2 pr-3">Destinatário</th>
                    <th className="py-2 pr-3">Tipo</th>
                    <th className="py-2 pr-3">Métricas</th>
                    <th className="py-2 pr-3">ID Resend</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={`${log.resend_id}-${idx}`} className="border-b border-gray-100" data-testid={`notifications-log-row-${idx}`}>
                      <td className="py-2.5 pr-3 text-gray-700 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          {formatDate(log.sent_at)}
                        </div>
                      </td>
                      <td className="py-2.5 pr-3 text-gray-700">{log.email || '—'}</td>
                      <td className="py-2.5 pr-3">
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                          {log.type || 'weekly_digest'}
                        </Badge>
                      </td>
                      <td className="py-2.5 pr-3">
                        {log.metrics ? (
                          <div className="flex flex-wrap gap-1">
                            <StatPill label="Proj" value={log.metrics.active_projects} accent="gray" />
                            <StatPill label="Pend" value={log.metrics.pending_tasks} accent="gray" />
                            <StatPill label="Atras" value={log.metrics.overdue_tasks} accent={log.metrics.overdue_tasks ? 'red' : 'gray'} />
                            <StatPill label="Crít" value={log.metrics.critical_deadlines} accent={log.metrics.critical_deadlines ? 'amber' : 'gray'} />
                          </div>
                        ) : '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-xs text-gray-500 font-mono">{log.resend_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
